import { z } from 'zod';
import { ORG_UNIT_SUBTYPES, ORG_UNIT_TYPES } from '../constants/identity.constant';
import { ORG_UNIT_ELEVATIONS } from '../constants/orgUnitElevation.constant';
import { createPaginationSchema, objectIdSchema } from './common.schema';

/**
 * The org tree (PA_REQUIREMENTS §4.2).
 *
 * Adjacency (`parentId`) plus a materialised `ancestors` array on every node, so a
 * subtree query is one indexed lookup instead of a recursive walk. `depth` is derived
 * from `ancestors.length` and is DYNAMIC — the tree is COMMISSIONERATE → DIVISION →
 * UNIT today and may be deeper tomorrow. Nothing anywhere hardcodes three levels.
 */
export const orgUnitBaseSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(160),
  shortCode: z
    .string()
    .trim()
    .min(1, 'Short code is required')
    .max(24)
    .transform((value) => value.toUpperCase()),
  type: z.enum(ORG_UNIT_TYPES),
  /**
   * ⚠️ [amended P2] NO `.default()` HERE. It lives on `createOrgUnitSchema` below.
   *
   * In zod 4 `.partial()` does NOT strip a `.default()` — it produces
   * `ZodOptional<ZodDefault<…>>`, and the default still fires on a missing key. With the
   * default on this base schema, `updateOrgUnitSchema.safeParse({})` succeeded with
   * `{ subType: 'OTHER' }`, which made its own "at least one field" refine unreachable
   * AND silently reset a TRAFFIC_GUARD unit to OTHER on an empty PATCH.
   *
   * The rule, which CLAUDE.md §6 now states: a base schema that gets `.partial()`-ed
   * carries no defaults. Defaults go on the create schema, added with `.extend`.
   */
  subType: z.enum(ORG_UNIT_SUBTYPES),
});

export const createOrgUnitSchema = orgUnitBaseSchema.extend({
  subType: z.enum(ORG_UNIT_SUBTYPES).default('OTHER'),
  /**
   * `null` creates a ROOT node, which only a global-scope caller may do — a nodal
   * admin creating a root would be creating a tree outside their own subtree, which is
   * to say outside anyone's.
   */
  parentId: objectIdSchema.nullable().default(null),
});

export const updateOrgUnitSchema = orgUnitBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

/** Reparent is its own endpoint because it rewrites every descendant's ancestors. */
export const reparentOrgUnitSchema = z.object({
  parentId: objectIdSchema.nullable(),
});

/**
 * ⚠️ [C5c §11d] ELEVATION HAS ITS OWN ENDPOINT, AND `updateOrgUnitSchema` DOES NOT DECLARE IT.
 *
 * `PATCH /org-units/:id` is `org.write`, which a NODAL_ADMIN holds, and `orgUnitScopeFilter` is the
 * INCLUSIVE subtree — so a key on that schema would let a nodal admin raise their own unit. Stripped
 * there by zod (unknown keys are stripped), accepted only here, behind `org.elevate`.
 */
export const setOrgUnitElevationSchema = z.object({ elevation: z.enum(ORG_UNIT_ELEVATIONS) });

export const orgUnitSchema = orgUnitBaseSchema.extend({
  id: objectIdSchema,
  parentId: objectIdSchema.nullable(),
  /** [C5c §11] What the unit is set to. Always present; `NONE` for almost every unit. */
  elevation: z.enum(ORG_UNIT_ELEVATIONS),
  /** Root-first and EXCLUDING self, so `depth === ancestors.length`. */
  ancestors: z.array(objectIdSchema),
  depth: z.number().int().min(0),
  /**
   * ─── [amended SEED] READ-ONLY, AND DELIBERATELY NOT ON `orgUnitBaseSchema` ─────
   *
   * `sectionCode` is a police station's operational code ('A', 'D1', 'KSB', 'WPPS'),
   * unique across the whole establishment; `psCode` is reserved and unwritten. Both live
   * on UNIT nodes only. They are added HERE, to the read DTO, and to nothing else — so
   * they reach neither `createOrgUnitSchema` nor `updateOrgUnitSchema`. Three reasons,
   * in order of weight:
   *
   *  1. A PATCH MAY OMIT `type`. `updateOrgUnitSchema` is `orgUnitBaseSchema.partial()`,
   *     so a refine there judges a payload that need not contain the field the rule is
   *     about: `PATCH { "sectionCode": "XYZ" }` on a DIVISION would carry no `type` to
   *     contradict, and pass. The model cannot rescue it either — mongoose runs update
   *     validators with the QUERY as `this`, so a validator cannot read the STORED type
   *     (`orgUnit.model.ts` states the measurement). "Enforced in the schema rather than
   *     by convention" would be half-true, which is worse than the fields being
   *     unreachable over HTTP.
   *  2. ⚠️ MEASURED on zod 4.4.3: `z.object({…}).refine(fn).partial()` THROWS
   *     `.partial() cannot be used on object schemas containing refinements` — AT MODULE
   *     LOAD, taking down every importer of `@pams/shared` at once, with an error naming
   *     `.partial()` rather than the refine that caused it. Line 47 below is literally
   *     `orgUnitBaseSchema.partial()`, so the base cannot carry a cross-field rule at all.
   *  3. The only writer is `pnpm seed:establishment`. A second writer of a globally
   *     unique code, reachable over HTTP, is a way for the two to disagree and nothing
   *     more.
   *
   * `.optional()` rather than `.nullable()`: absent stays absent, which is the rule
   * storage follows — the partial unique index is on `$type: 'string'` and the model's
   * setter omits the path rather than writing null.
   *
   * ⚠️ NOT added to `orgUnitSummarySchema` below. Two services build that DTO by hand
   * behind narrowed `.select()` projections, so an optional field there would make the
   * same unit carry a code in one payload and not in another.
   */
  sectionCode: z.string().optional(),
  psCode: z.string().optional(),
  /**
   * [amended INGEST] READ-ONLY, for the same three reasons `sectionCode` above is, and
   * one more: an alias only means anything against the section code it resolves TO, so a
   * writer that could add one without owning the code would be able to point two
   * stations at one spelling. The only writer is `pnpm seed:establishment`.
   *
   * ⚠️ REQUIRED, AND NEITHER `.optional()` NOR `.default([])`. Divergence #100's rule,
   * one schema over: an optional field DRIFTS behind whoever forgets it, a required one
   * breaks the build until every builder supplies it. `toOrgUnitDto` always emits the
   * array — `[]` is a real answer, not an absence — so required is what the payload
   * actually is.
   *
   * `.default([])` would have been worse than either: it makes the parsed OUTPUT type
   * required while the INPUT type stays optional, so `z.infer` and the DTO stop being the
   * same shape and a fixture that compiles in one direction fails in the other.
   */
  aliases: z.array(z.string()),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ORG_UNIT_SORT_FIELDS = ['name', 'shortCode', 'depth', 'createdAt'] as const;
export type OrgUnitSortField = (typeof ORG_UNIT_SORT_FIELDS)[number];

export const listOrgUnitsQuerySchema = createPaginationSchema(ORG_UNIT_SORT_FIELDS, 'name')
  .extend({
    type: z.enum(ORG_UNIT_TYPES).optional(),
    subType: z.enum(ORG_UNIT_SUBTYPES).optional(),
    parentId: objectIdSchema.optional(),
    /**
     * NARROWS the caller's scope; it can never widen it. The scope filter and this
     * filter are ANDed in the query, so passing a sibling division's id returns nothing
     * rather than that division's officers.
     */
    rootId: objectIdSchema.optional(),
    includeInactive: z.stringbool().default(false),
    search: z.string().trim().min(1).max(160).optional(),
  })
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });

/**
 * The tree endpoint returns the SAME flat page shape, ordered `depth ASC, name ASC` so
 * a parent always arrives before its children and a client can assemble the tree
 * incrementally across pages.
 *
 * Not a nested payload, for two reasons that are not preferences:
 *  1. "Page 2 of a tree" is not a thing — a nested shape forces an unbounded find(),
 *     which CLAUDE.md §7 forbids with no exception for trees.
 *  2. A recursive DTO needs `z.lazy` plus a hand-written type annotation, and
 *     CLAUDE.md §6 says DTOs are `z.infer` and are never hand-written.
 */
export const orgUnitTreeQuerySchema = z.object({
  rootId: objectIdSchema.optional(),
  maxDepth: z.coerce.number().int().min(0).max(12).optional(),
  includeInactive: z.stringbool().default(false),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(500),
});

/**
 * ─── [amended P1b] `GET /org-units/counts` ───────────────────────────────────────
 *
 * Officer and nodal counts per unit, exact at any scale, in ONE request.
 *
 * This replaces a client-side census that probed `/officers` page by page and gave up
 * above 1 500 officers in scope, at which point the subtree column DISAPPEARED. A
 * column that vanishes once the data is real is worse than no column, and importing a
 * single commissionerate crosses that threshold on the first upload.
 *
 * ⚠️ THE DESCENDANT COUNTS EXCLUDE THE UNIT ITSELF. The inclusive number a screen
 * usually wants is `officerCount + descendantOfficerCount`. Stated here rather than
 * left to be inferred, because "descendant" and "subtree" differ by exactly one node
 * and the difference is invisible in any test with a single level.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const orgUnitCountsSchema = z.object({
  orgUnitId: objectIdSchema,
  /** Officers posted to this unit and no other. */
  officerCount: z.number().int().min(0),
  /** Of those, the ones with dashboard access — `accessRole !== 'NONE'` (FR-3.2). */
  nodalCount: z.number().int().min(0),
  /** Everything strictly beneath this unit. EXCLUSIVE of the unit itself. */
  descendantOfficerCount: z.number().int().min(0),
  descendantNodalCount: z.number().int().min(0),
});

export const orgUnitCountsQuerySchema = z.object({
  /**
   * Deactivated officers are excluded by DEFAULT, which differs from the client-side
   * census this replaces — that one counted them, because `/officers` returns both and
   * it never asked otherwise. Active-only is the number the screen means, and it is
   * the same population the deactivation guard counts before refusing to close a unit.
   */
  officerStatus: z.enum(['active', 'all']).default('active'),
  /** ANDed with the caller's scope, exactly like `listOrgUnits`. It can only narrow. */
  rootId: objectIdSchema.optional(),
});

/**
 * NOT `Paginated`, and not a bare array.
 *
 * A page of counts is not a coherent answer: the screen needs the whole map to render
 * one column, so page two arriving late means the tree renders wrong numbers and then
 * corrects itself. The bound is structural instead — the same `MAX_SUBTREE_NODES` that
 * bounds scope resolution — and it THROWS rather than truncating, because a truncated
 * count list under-reports silently. An object rather than a bare array so a `total`
 * or a `truncated` flag can be added later without breaking a caller.
 */
export const orgUnitCountsResponseSchema = z.object({
  items: z.array(orgUnitCountsSchema),
});

export const orgUnitSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  shortCode: z.string(),
  type: z.enum(ORG_UNIT_TYPES),
  /**
   * ⚠️ [amended P5a] ADMITTED, WHERE `sectionCode` AND `psCode` ABOVE ARE STILL REFUSED — AND
   * THE DIFFERENCE IS THAT THIS ONE CANNOT BE ABSENT.
   *
   * That refusal is about OPTIONAL fields: those two are absent on most nodes, so a summary
   * carrying them would make the same unit render a code in one payload and not in another.
   * `sub_type` is `required: true` with `default: 'OTHER'` on the model, so every document has
   * one and every builder of this DTO reads a full document — checked: all three call sites go
   * through `toOrgUnitSummaryDto` with no narrowed `.select()` behind them.
   *
   * It is here because a police station is `type: 'UNIT'` + `subType: 'PS'` (PA_REQUIREMENTS
   * §4.2), and a client told only the type cannot tell a police station from a traffic guard.
   * The officer app's supplied contract asked for `type: "POLICE_STATION"`, which is the same
   * question asked in a shape this system does not have; returning both answers it honestly
   * rather than inventing a compound value.
   */
  subType: z.enum(ORG_UNIT_SUBTYPES),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1b §4] ORG-UNIT SEARCH — THE ONE READ ON THIS COLLECTION THAT TAKES NO SCOPE.
 *
 *  ⚠️ **A UNIT EXISTING IS NOT SENSITIVE; A POLICE ARRANGEMENT ADDRESSED TO IT IS.**
 *
 *  That is `place.read`'s argument, applied to the establishment — and the establishment is
 *  the WEAKER case: Kolkata Police's division and station list is published, is printed on
 *  every order sheet's addressee block, and is already reachable by any officer who has stood
 *  a duty. FR-3.6's isolation lives on the arrangement and is unchanged;
 *  `visible_org_unit_ids` still decides which orders exist for a caller.
 *
 *  It exists because §4c requires an associated unit to be choosable from ANY division,
 *  including one with no allocation on the order. `GET /org-units/tree` is scoped, so a
 *  control built on it structurally cannot return the answer.
 *
 *  ⚠️ **WHAT IS NOT DISCLOSED IS STRUCTURE.** The payload is `{id, name, shortCode, type,
 *  subType}` plus the DIVISION's summary — and no `parentId`, no `ancestors`, no `depth`, no
 *  counts and no arrangement. It answers "does Alipore PS exist, and whose division is it
 *  in", which is exactly what §4a and §1b require an operator to see, and it cannot be
 *  assembled into another division's org chart.
 *
 *  ⚠️ **`GET /org-units/tree` STAYS SCOPED, AND THIS IS NOT A WAY ROUND IT.** The tree returns
 *  `ancestors` and `depth` and every picker in the product is built on it; this returns at
 *  most twenty flat rows behind a three-character minimum. `scoping.test.ts`'s
 *  `describe('the org tree')` block must stay green UNEDITED — if it ever needs editing, the
 *  change is wrong.
 *
 *  ⚠️ **THE THREE-CHARACTER MINIMUM IS A CONTROL AS MUCH AS A BOUND.** Below it every unit
 *  matches, which turns this into an unscoped `GET /org-units`. Sweeping the name space is
 *  metered by the global limiter, like every other read.
 *
 *  ⚠️ **AND NO DERIVED SEARCH COLUMN, UNLIKE `places`.** [INGEST] states the difference: a
 *  place is looked up by PREFIX from a typeahead over a collection that grows without bound;
 *  `org_units` is about 170 rows. A bounded scan is the correct answer here, and a derived
 *  column would be a second thing to keep in step for a query that does not need it. The
 *  condition under which that stops being true, written down rather than left to be
 *  re-derived: **if `org_units` ever exceeds about 2,000 rows** (`MAX_SUBTREE_NODES`' own
 *  order of magnitude), this needs `search_terms` and a multikey index.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ORG_UNIT_SEARCH_MIN_CHARS = 3;
export const ORG_UNIT_SEARCH_MAX_RESULTS = 20;

export const orgUnitSearchQuerySchema = z.object({
  q: z.string().trim().min(ORG_UNIT_SEARCH_MIN_CHARS).max(80),
  limit: z.coerce.number().int().min(1).max(ORG_UNIT_SEARCH_MAX_RESULTS).default(ORG_UNIT_SEARCH_MAX_RESULTS),
});

export const orgUnitSearchRowSchema = z.object({
  unit: orgUnitSummarySchema,
  /** `null` is a real answer — a unit need not sit under a division at all. */
  division: orgUnitSummarySchema.nullable(),
});

export const orgUnitSearchSchema = z.object({ items: z.array(orgUnitSearchRowSchema) });
